import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { T, Icon, stitchPill, stitchCard, StitchIconBtn } from '../parts/stitch';
import { useProjectData } from '../ProjectDataContext';

function FrameioPanel() {
  const {
    frameioVideos,
    downloadingVideoId,
    handleVideoDownload,
  } = useProjectData();

  return (
    <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            {frameioVideos.map((video) => (
              <Box
                key={video.id}
                sx={{
                  ...stitchCard,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: '0 8px 24px rgba(45,51,53,0.08)', transform: 'translateY(-2px)' },
                }}
                onClick={() => video.player_url && window.open(video.player_url, '_blank')}
              >
                {/* Thumbnail */}
                <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#1a1a2e' }}>
                  {video.thumb_large || video.thumb_small ? (
                    <Box
                      component="img"
                      src={video.thumb_large || video.thumb_small}
                      alt={video.filename}
                      sx={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon name="videocam" size={48} sx={{ color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                  )}
                  {/* Play overlay */}
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s',
                    '&:hover': { opacity: 1 },
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  }}>
                    <Icon name="play_arrow" size={56} sx={{ color: '#fff' }} />
                  </Box>
                  {/* Duration badge */}
                  {video.duration && (
                    <Box sx={{
                      position: 'absolute', bottom: 6, right: 6,
                      backgroundColor: 'rgba(0,0,0,0.75)', color: '#fff',
                      px: 0.75, py: 0.25, borderRadius: '0.25rem', fontSize: '0.7rem',
                      fontFamily: T.fontBody,
                    }}>
                      {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
                    </Box>
                  )}
                  {/* Label badge */}
                  {video.label && (
                    <Box sx={{
                      position: 'absolute', top: 6, right: 6,
                    }}>
                      <Typography component="span" sx={stitchPill(
                        '#fff',
                        video.label === 'approved' ? T.emerald : video.label === 'needs_review' ? T.amber : T.outline
                      )}>
                        {video.label}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Info */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, flex: 1, minWidth: 0 }} noWrap title={video.filename}>
                      {video.filename}
                    </Typography>
                    <StitchIconBtn
                      onClick={(e) => handleVideoDownload(e, video)}
                      disabled={downloadingVideoId === video.frameio_asset_id}
                      title="Download video"
                      sx={{ width: 28, height: 28, color: T.outline, '&:hover': { color: T.primary } }}
                    >
                      {downloadingVideoId === video.frameio_asset_id
                        ? <CircularProgress size={16} />
                        : <Icon name="download" size={18} />
                      }
                    </StitchIconBtn>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                    {video.version_number && (
                      <Typography component="span" sx={stitchPill('#7c3aed', '#ede9fe')}>
                        v{video.version_number}
                      </Typography>
                    )}
                    {video.language && (
                      <Typography component="span" sx={stitchPill('#2563eb', '#dbeafe')}>
                        {video.language}
                      </Typography>
                    )}
                    {video.variant && (
                      <Typography component="span" sx={stitchPill('#d97706', T.amberBg)}>
                        {video.variant}
                      </Typography>
                    )}
                    {video.filesize && (
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, ml: 'auto' }}>
                        {(video.filesize / 1024 / 1024).toFixed(0)} MB
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
    </>
  );
}

export default FrameioPanel;
